import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { NxDialogService, NxModalModule } from '@aposin/ng-aquila/modal';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import Cookies from 'js-cookie';

import { HeaderComponent } from '#components/header/header.component';
import {
  PREFERRED_LOB_COOKIE_DURATION_IN_DAYS,
  PREFERRED_LOB_COOKIE_NAME,
} from '#components/line-of-business-selector/line-of-business-selector.component';
import { environment } from '#environments/environment';
import { SessionExpiredModalComponent } from '#modals/session-expired-modal';
import { UserUnavailableModalComponent } from '#modals/user-unavailable-modal';
import SessionExpiredService from '#services/session-expired/session-expired.service';
import UserService from '#services/user/user.service';

export const authCodeFlowConfig: AuthConfig = {
  issuer: `${environment.apiUrl}/v1/oidc`,
  strictDiscoveryDocumentValidation: false,
  skipIssuerCheck: true,
  redirectUri: window.location.origin + window.location.pathname,
  clientId: environment.clientId,
  responseType: 'code',
  scope: 'openid',
  showDebugInformation: false,
};

function injectScript(src: string) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.addEventListener('load', resolve);
    script.addEventListener('error', (e) => reject(Error(e.error)));
    document.head.appendChild(script);
  });
}

@Component({
  selector: 'kyc-app-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet, NxSpinnerModule, NxModalModule, HeaderComponent],
})
export class AppComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly oauthService = inject(OAuthService);
  private readonly sessionExpiredService = inject(SessionExpiredService);
  private readonly dialogService = inject(NxDialogService);

  protected internalUserError = false;
  protected readonly isLoadingInternalUser = signal(true);

  protected readonly isLoading = computed(() => {
    return this.isLoadingInternalUser() && this.userService.isLoadingPermissions();
  });

  constructor() {
    this.oauthService.configure(authCodeFlowConfig);
    this.oauthService.setupAutomaticSilentRefresh();
    this.init();

    const cookieStoredLob = Cookies.get(PREFERRED_LOB_COOKIE_NAME) ?? '';
    Cookies.set(PREFERRED_LOB_COOKIE_NAME, cookieStoredLob, {
      expires: PREFERRED_LOB_COOKIE_DURATION_IN_DAYS,
    });

    this.sessionExpiredService.isSessionExpired$.subscribe((isSessionExpired) => {
      if (isSessionExpired) {
        this.dialogService.open(SessionExpiredModalComponent, {
          data: {
            reload: () => this.reload(),
          },
          disableClose: true,
        });
      }
    });
  }

  private init() {
    this.isLoadingInternalUser.set(true);
    this.oauthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(async () => {
        if (!this.oauthService.hasValidAccessToken()) {
          this.oauthService.initLoginFlow();
        }
        this.sessionExpiredService.setSessionStarted();
        if (this.gadId) {
          await this.fetchUserService(this.gadId);
        }
      })
      .catch((err) => {
        this.internalUserError = true;
        console.error('Failed to fetch user informations:', err);
        this.dialogService.open(UserUnavailableModalComponent, {
          data: {
            reload: () => this.reload(),
          },
          disableClose: true,
        });
      })
      .finally(() => this.isLoadingInternalUser.set(false));
  }

  async fetchUserService(gadId: string): Promise<void> {
    const user = await this.userService.fetchInternalUser(gadId);
    if (!user) throw Error(`No user found for gadId: ${gadId}`);
    this.userService.setInternalUser(user);
  }

  get gadId(): string | undefined {
    return this.userService.getGadId();
  }

  get isLoggedIn(): boolean {
    return !environment.production || this.oauthService.hasValidAccessToken();
  }

  private reload() {
    if (!this.oauthService.hasValidAccessToken()) this.oauthService.logOut(true);
    window.location.reload();
  }

  ngOnInit() {
    if (environment.dynatraceUrl && environment.dynatraceRuxitUrl) {
      Promise.all([injectScript(environment.dynatraceUrl), injectScript(environment.dynatraceRuxitUrl)]).catch(() =>
        console.error('Failed to load or inject dynatrace scripts'),
      );
    }
  }
}
